import React, { useEffect, useState, useRef } from 'react';
import {
  Box, CssBaseline, Typography, Avatar, IconButton, Stack, Button, Paper, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, MenuItem, 
  Select, FormControl, InputLabel, Chip, TextField, InputAdornment, Grid, 
  Tooltip, CircularProgress, Snackbar, Alert, Dialog, DialogTitle, 
  DialogContent, DialogActions, Breadcrumbs, Link, Backdrop
} from '@mui/material';
import {
  School, Search, ArrowBack, Save, WarningAmber, FilterList
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const colors = {
  pageBg: '#F8F9FA',
  sidebarBg: '#F1F2F6',
  filterBg: "#FFF9E6",
  accent: '#FFC107',
  tableHeader: '#FDF7E2',
  textMain: '#5D4037',
  examBottom: '#FFEBEE', // Red
  examMiddle: '#FFF9C4', // Yellow
  examTop: '#E8F5E9',    // Green
  greyBg: '#F5F5F5'
};

const classList = [
    { id: "Tahun 1", title: "Tahun 1" }, { id: "Tahun 2", title: "Tahun 2" },
    { id: "Tahun 3", title: "Tahun 3" }, { id: "Tahun 4", title: "Tahun 4" },
    { id: "Tahun 5", title: "Tahun 5" }, { id: "Tahun 6", title: "Tahun 6" },
    { id: "Tingkatan 1", title: "Tingkatan 1" }, { id: "Tingkatan 2", title: "Tingkatan 2" },
    { id: "Tingkatan 3", title: "Tingkatan 3" }, { id: "Tingkatan 4", title: "Tingkatan 4" },
    { id: "Tingkatan 5", title: "Tingkatan 5" }
];

const monthList = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const yearList = Array.from({ length: 5 }, (_, i) => (2024 + i).toString());

const columnsUPSR = [
    { id: 'studentName', name: 'Nama' },
    { id: 'Bahasa Melayu', name: 'B. Melayu' },
    { id: 'English', name: 'English' },
    { id: 'Matematik', name: 'Matematik' },
    { id: 'Sains', name: 'Sains' }
];

const columnsPMR = [
    { id: 'studentName', name: 'Nama' },
    { id: 'Bahasa Melayu', name: 'B. Melayu' },
    { id: 'English', name: 'English' },
    { id: 'Matematik', name: 'Matematik' },
    { id: 'Sains', name: 'Sains' },
    { id: 'Sejarah', name: 'Sejarah' }
];

const columnsSPM = [
    { id: 'studentName', name: 'Nama' },
    { id: 'Bahasa Melayu', name: 'B. Melayu' },
    { id: 'English', name: 'English' },
    { id: 'Matematik', name: 'Matematik' },
    { id: 'Sains', name: 'Sains' },
    { id: 'Sejarah', name: 'Sejarah' },
    { id: 'Akaun', name: 'Akaun' },
    { id: 'Add Math', name: 'Add Math' },
    { id: 'Biologi', name: 'Biologi' },
    { id: 'Kimia', name: 'Kimia' },
    { id: 'Fizik', name: 'Fizik' },
    { id: 'Perniagaan', name: 'Perniagaan' },
    { id: 'Ekonomi', name: 'Ekonomi' },
];

export default function Exam() {
  const navigate = useNavigate();
  
  // --- Logic State ---
  const [filtered, setFiltered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [classroom, setClassroom] = useState('NA');
  const [month, setMonth] = useState('NA');
  const [year, setYear] = useState('NA');
  const [studentData, setStudentData] = useState([]);
  const [filterSearch, setFilterSearch] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    localStorage.setItem("current_tab", "New Exam");
  }, []);

  // --- API Logic ---
  const fetchExamResults = () => {
    if (classroom === "NA" || year === "NA" || month === "NA") {
        setError("Please select all fields");
        return;
    }
    setLoading(true);
    setError("");
    
    fetch(`https://www.geniusmileniaeducation.link/db-api/exams/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batch: classroom, month: month, year: year })
    })
    .then((res) => res.json())
    .then((json) => {
        if (json.data) {
            const exams = json.data;
            const studentMap = {};
            exams.forEach(exam => {
                const subject = exam.subject;
                exam.studentMarks.forEach(studentMark => {
                    const { studentId, studentName, marks } = studentMark;
                    if (!studentMap[studentId]) {
                        studentMap[studentId] = { studentId, studentName };
                    }
                    studentMap[studentId][subject] = marks || "";
                });
            });
            setStudentData(Object.values(studentMap));
            setFiltered(true);
        } else {
            setStudentData([]);
            setFiltered(true);
        }
        setLoading(false);
    }).catch(() => setLoading(false));
  };

  const getDynamicColumns = () => {
    if (classroom.includes("Tahun")) return columnsUPSR;
    if (["Tingkatan 1", "Tingkatan 2", "Tingkatan 3"].includes(classroom)) return columnsPMR;
    if (["Tingkatan 4", "Tingkatan 5"].includes(classroom)) return columnsSPM;
    return [];
  };

  const getCellStyles = (value) => {
    if (value === "" || value === undefined) return {};
    if (value === "TH" || value === "th") return { bgcolor: '#E0E0E0', color: '#757575' };
    
    const n = parseInt(value);
    if (isNaN(n)) return {};

    if (n <= 39) return { bgcolor: colors.examBottom, color: '#C62828' };
    
    // SPM logic uses 70 for Top, UPSR/PMR uses 80
    const topThreshold = (classroom.includes("Tingkatan 4") || classroom.includes("Tingkatan 5")) ? 70 : 80;
    const midThreshold = (classroom.includes("Tingkatan 4") || classroom.includes("Tingkatan 5")) ? 69 : 79;

    if (n >= 40 && n <= midThreshold) return { bgcolor: colors.examMiddle, color: '#827717' };
    if (n >= topThreshold && n <= 100) return { bgcolor: colors.examTop, color: '#2E7D32' };
    
    return {};
  };

  return (
    <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
      <CssBaseline />
      
      <Backdrop sx={{ color: colors.accent, zIndex: 1500 }} open={loading}>
        <Stack alignItems="center" spacing={2}>
            <CircularProgress color="inherit" />
            <Typography variant="h6" color="white" sx={{ fontWeight: 'bold' }}>Fetching Exam Data...</Typography>
        </Stack>
      </Backdrop>

      <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>
        
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Breadcrumbs sx={{ mb: 1 }}>
                <Link underline="hover" color="inherit" onClick={() => navigate("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                <Typography color="text.primary">Exam Results</Typography>
            </Breadcrumbs>
            <Typography variant="h4" fontWeight="bold" color={colors.textMain}>
                {filtered ? `${classroom} - ${month} ${year}` : 'Exam Management'}
            </Typography>
          </Box>
          {filtered && (
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => { setFiltered(false); setStudentData([]); }} sx={{ borderRadius: 2 }}>Change Filter</Button>
          )}
        </Stack>

        {!filtered ? (
          /* --- FILTER SELECTION UI --- */
          <Paper elevation={0} sx={{ p: 4, bgcolor: colors.filterBg, borderRadius: 4, textAlign: 'center', border: '1px solid #F0E6CC' }}>
            <School sx={{ fontSize: 60, color: colors.accent, mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" mb={3} color={colors.textMain}>Select Session Details</Typography>
            
            {error && <Alert severity="error" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>{error}</Alert>}

            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                    <InputLabel>Class</InputLabel>
                    <Select value={classroom} label="Class" onChange={(e) => setClassroom(e.target.value)} sx={{ bgcolor: 'white' }}>
                        <MenuItem value="NA">None</MenuItem>
                        {classList.map(c => <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>)}
                    </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                    <InputLabel>Month</InputLabel>
                    <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)} sx={{ bgcolor: 'white' }}>
                        <MenuItem value="NA">None</MenuItem>
                        {monthList.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                    </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                    <InputLabel>Year</InputLabel>
                    <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)} sx={{ bgcolor: 'white' }}>
                        <MenuItem value="NA">None</MenuItem>
                        {yearList.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                    </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Button variant="contained" onClick={fetchExamResults} sx={{ mt: 4, bgcolor: colors.accent, color: 'black', fontWeight: 'bold', px: 6, py: 1.5, borderRadius: 3 }}>
                Search Results
            </Button>
          </Paper>
        ) : (
          /* --- DATA TABLE UI --- */
          <>
            <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: colors.filterBg, borderRadius: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
              <FilterList sx={{ color: colors.textMain }} />
              <TextField size="small" placeholder="Search Student Name..." fullWidth onChange={(e) => setFilterSearch(e.target.value.toUpperCase())} sx={{ bgcolor: 'white', borderRadius: 2 }} InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            </Paper>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #F0E6CC', borderRadius: 4, maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {getDynamicColumns().map((col) => (
                      <TableCell 
                        key={col.id} 
                        sx={{ 
                          bgcolor: col.id === 'studentName' ? colors.accent : colors.tableHeader, 
                          fontWeight: 'bold', 
                          zIndex: col.id === 'studentName' ? 11 : 10, 
                          left: col.id === 'studentName' ? 0 : 'auto', 
                          position: col.id === 'studentName' ? 'sticky' : 'static',
                          borderRight: '1px solid #E0E0E0'
                        }}
                      >
                        {col.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {studentData.length === 0 ? (
                    <TableRow><TableCell colSpan={20} align="center" sx={{ py: 4 }}>No records found for this session.</TableCell></TableRow>
                  ) : (
                    studentData.filter(s => s.studentName?.toUpperCase().includes(filterSearch)).map((s, i) => (
                        <TableRow key={i} hover>
                          {getDynamicColumns().map((col) => {
                            const isName = col.id === 'studentName';
                            const val = s[col.id];
                            const cellStyle = getCellStyles(val);
                            
                            return (
                              <TableCell 
                                key={col.id} 
                                align={isName ? "left" : "center"}
                                sx={{ 
                                    ...cellStyle,
                                    fontWeight: isName ? "bold" : "normal",
                                    position: isName ? 'sticky' : 'static', 
                                    left: isName ? 0 : 'auto', 
                                    bgcolor: isName ? 'white' : cellStyle.bgcolor, 
                                    zIndex: isName ? 5 : 1,
                                    borderRight: '1px solid #F0F0F0'
                                }}
                              >
                                {isName ? (
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Avatar sx={{ bgcolor: colors.tableHeader, color: colors.textMain, fontSize: 11, width: 28, height: 28 }}>
                                            {s.studentName?.substring(0, 1)}
                                        </Avatar>
                                        <Typography variant="body2" fontWeight="bold">{s.studentName}</Typography>
                                    </Stack>
                                ) : val}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Stack direction="row" spacing={2} mt={3} justifyContent="center">
              <Chip label="Critical (0-39)" sx={{ bgcolor: colors.examBottom, color: '#C62828', fontWeight: 'bold' }} />
              <Chip label="Satisfactory" sx={{ bgcolor: colors.examMiddle, color: '#827717', fontWeight: 'bold' }} />
              <Chip label="Excellent" sx={{ bgcolor: colors.examTop, color: '#2E7D32', fontWeight: 'bold' }} />
              <Chip label="Absent (TH)" sx={{ bgcolor: '#E0E0E0', color: '#757575', fontWeight: 'bold' }} />
            </Stack>
          </>
        )}
      </Box>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError("")}>
        <Alert severity="error" variant="filled">{error}</Alert>
      </Snackbar>
    </Box>
  );
}